import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Sun,
  Plus,
  Search,
  Calendar,
  Euro,
  Clock,
  CheckCircle2,
  XCircle,
  Pause,
  FileText,
  Home,
  Zap,
  Trash2,
  ShoppingCart,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Statuts et couleurs
const STATUTS = {
  devis: { label: "Devis", icon: FileText, color: "bg-blue-100 text-blue-800" },
  accepte: { label: "Accepté", icon: CheckCircle2, color: "bg-green-100 text-green-800" },
  en_cours: { label: "En cours", icon: Clock, color: "bg-orange-100 text-orange-800" },
  pose_finalisee: { label: "Pose finalisée", icon: CheckCircle2, color: "bg-teal-100 text-teal-800" },
  termine: { label: "Terminé", icon: CheckCircle2, color: "bg-emerald-100 text-emerald-800" },
  annule: { label: "Annulé", icon: XCircle, color: "bg-red-100 text-red-800" },
  suspendu: { label: "Suspendu", icon: Pause, color: "bg-gray-100 text-gray-800" },
};

const TYPES_PROJET = {
  photovoltaique: { label: "Photovoltaïque", icon: Sun, color: "text-yellow-600" },
  pompe_chaleur: { label: "Pompe à chaleur", icon: Home, color: "text-blue-600" },
  isolation: { label: "Isolation", icon: Home, color: "text-gray-600" },
  chaudiere: { label: "Chaudière", icon: Zap, color: "text-orange-600" },
  vmc: { label: "VMC", icon: Zap, color: "text-purple-600" },
  chauffe_eau_thermodynamique: { label: "Chauffe-eau thermo", icon: Zap, color: "text-cyan-600" },
  climatisation: { label: "Climatisation", icon: Zap, color: "text-blue-400" },
};

const ProjetsPage = ({ user }) => {
  const [projets, setProjets] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [produitsCatalogue, setProduitsCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showProduitsDialog, setShowProduitsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingProjet, setEditingProjet] = useState(null);

  // Produits sélectionnés pour le projet
  const [produitsSelectionnes, setProduitsSelectionnes] = useState([]);

  // Nouveau projet par défaut
  const [newProjet, setNewProjet] = useState({
    client_id: "",
    type_projet: "photovoltaique",
    titre: "",
    description: "",
    adresse_installation: "",
    code_postal: "",
    ville: "",
    puissance_kwc: 0,
    nombre_panneaux: 0,
    produits: [],
    commercial_id: user?.id || "",
    statut: "devis",
    date_installation: "",
    montant_materiel_ht: 0,
    montant_main_oeuvre_ht: 0,
    cout_achat_materiel: 0,
    cout_achat_pose: 0,
  });

  useEffect(() => {
    fetchAll();
  }, []);

  // Auto-open create dialog from URL params (from ClientDetails) AFTER data loaded
  useEffect(() => {
    if (clients.length > 0) {
      const params = new URLSearchParams(window.location.search);
      if (params.get('create') === 'true') {
        const clientId = params.get('client_id');
        if (clientId) {
          const client = clients.find(c => c.id === clientId);
          if (client) {
            setNewProjet(prev => ({
              ...prev,
              client_id: clientId,
              adresse_installation: client.adresse || "",
              code_postal: client.code_postal || "",
              ville: client.ville || "",
            }));
          }
        }
        setShowCreateDialog(true);
        window.history.replaceState({}, '', '/projets');
      }
    }
  }, [clients]);

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem("token");
      const [projRes, cliRes, prodRes] = await Promise.all([
        axios.get(`${API}/projets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/produits`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProjets(projRes.data);
      setClients(cliRes.data);
      setProduitsCatalogue(prodRes.data);
      // Users est optionnel (admin/confirmateur seulement)
      try {
        const usrRes = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
        setUsers(usrRes.data);
      } catch (e) { /* pas admin, pas grave */ }
    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleAjouterProduit = (produit) => {
    const existe = produitsSelectionnes.find(p => p.produit_id === produit.id);
    if (existe) {
      // Augmenter la quantité
      setProduitsSelectionnes(produitsSelectionnes.map(p =>
        p.produit_id === produit.id ? { ...p, quantite: p.quantite + 1, prix_total_ht: (p.quantite + 1) * p.prix_unitaire_ht } : p
      ));
    } else {
      // Ajouter nouveau produit
      setProduitsSelectionnes([...produitsSelectionnes, {
        produit_id: produit.id,
        nom: produit.nom,
        reference: produit.reference,
        quantite: 1,
        prix_unitaire_ht: produit.prix_vente_ht,
        prix_total_ht: produit.prix_vente_ht,
        taux_tva: produit.taux_tva
      }]);
    }
    toast.success(`${produit.nom} ajouté`);
  };

  const handleRetirerProduit = (produitId) => {
    setProduitsSelectionnes(produitsSelectionnes.filter(p => p.produit_id !== produitId));
  };

  const handleChangerQuantite = (produitId, quantite) => {
    if (quantite <= 0) {
      handleRetirerProduit(produitId);
      return;
    }
    setProduitsSelectionnes(produitsSelectionnes.map(p =>
      p.produit_id === produitId ? { ...p, quantite, prix_total_ht: quantite * p.prix_unitaire_ht } : p
    ));
  };

  // 🆕 Modifier le prix unitaire d'un produit
  const handleModifierPrix = (produitId, nouveauPrix) => {
    setProduitsSelectionnes(produitsSelectionnes.map(p =>
      p.produit_id === produitId ? { 
        ...p, 
        prix_unitaire_ht: nouveauPrix, 
        prix_total_ht: p.quantite * nouveauPrix 
      } : p
    ));
  };

  // 🆕 Modifier le nom d'un produit
  const handleModifierNom = (produitId, nouveauNom) => {
    setProduitsSelectionnes(produitsSelectionnes.map(p =>
      p.produit_id === produitId ? { ...p, nom: nouveauNom } : p
    ));
  };

  // 🆕 Auto-remplir les données du client sélectionné
  const handleSelectClient = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setNewProjet({
        ...newProjet,
        client_id: clientId,
        adresse_installation: client.adresse || "",
        code_postal: client.code_postal || "",
        ville: client.ville || "",
      });
      toast.success("Données du client récupérées automatiquement");
    } else {
      setNewProjet({...newProjet, client_id: clientId});
    }
  };

  // 🆕 Éditer un projet existant
  const handleEditProjet = (projet) => {
    setEditingProjet(projet);
    setNewProjet({
      ...projet,
      client_id: projet.client_id || "",
      type_projet: projet.type_projet || "photovoltaique",
      titre: projet.titre || "",
      description: projet.description || "",
      adresse_installation: projet.adresse_installation || "",
      code_postal: projet.code_postal || "",
      ville: projet.ville || "",
      puissance_kwc: projet.puissance_kwc || 0,
      nombre_panneaux: projet.nombre_panneaux || 0,
      commercial_id: projet.commercial_id || user?.id || "",
      statut: projet.statut || "devis",
      date_installation: projet.date_installation ? new Date(projet.date_installation).toISOString().split('T')[0] : "",
      montant_materiel_ht: projet.montant_materiel_ht || 0,
      montant_main_oeuvre_ht: projet.montant_main_oeuvre_ht || 0,
      cout_achat_materiel: projet.cout_achat_materiel || 0,
      cout_achat_pose: projet.cout_achat_pose || 0,
    });
    setProduitsSelectionnes(projet.produits || []);
    setShowEditDialog(true);
  };

  const calculerTotaux = () => {
    let total_ht = 0;
    let total_tva = 0;

    produitsSelectionnes.forEach(p => {
      total_ht += p.prix_total_ht;
      total_tva += p.prix_total_ht * (p.taux_tva / 100);
    });

    // Ajouter les montants de main d'œuvre si saisis
    const main_oeuvre = parseFloat(newProjet.montant_main_oeuvre_ht) || 0;
    total_ht += main_oeuvre;

    return {
      total_ht: total_ht.toFixed(2),
      total_tva: total_tva.toFixed(2),
      total_ttc: (total_ht + total_tva).toFixed(2)
    };
  };

  const handleCreateProjet = async () => {
    if (!newProjet.client_id || !newProjet.titre) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    if (produitsSelectionnes.length === 0) {
      toast.error("Veuillez ajouter au moins un produit");
      return;
    }

    const totaux = calculerTotaux();

    // Calculer montant matériel HT à partir des produits
    const montant_materiel_ht = produitsSelectionnes.reduce((sum, p) => sum + p.prix_total_ht, 0);

    const projetData = {
      ...newProjet,
      produits: produitsSelectionnes,
      montant_materiel_ht: montant_materiel_ht,
      montant_main_oeuvre_ht: parseFloat(newProjet.montant_main_oeuvre_ht) || 0,
      cout_achat_materiel: parseFloat(newProjet.cout_achat_materiel) || 0,
      cout_achat_pose: parseFloat(newProjet.cout_achat_pose) || 0,
      montant_total_ht: parseFloat(totaux.total_ht),
      montant_tva: parseFloat(totaux.total_tva),
      montant_ttc: parseFloat(totaux.total_ttc),
    };

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/projets`, projetData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Projet créé avec succès !");
      setShowCreateDialog(false);
      fetchAll();
      resetForm();
    } catch (error) {
      console.error("Erreur création projet:", error);
      toast.error("Erreur lors de la création du projet");
    }
  };

  // 🆕 Mise à jour d'un projet existant
  const handleUpdateProjet = async () => {
    if (!newProjet.client_id || !newProjet.titre) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    if (produitsSelectionnes.length === 0) {
      toast.error("Veuillez ajouter au moins un produit");
      return;
    }

    const totaux = calculerTotaux();

    // Calculer montant matériel HT à partir des produits
    const montant_materiel_ht = produitsSelectionnes.reduce((sum, p) => sum + p.prix_total_ht, 0);

    const projetData = {
      ...newProjet,
      produits: produitsSelectionnes,
      montant_materiel_ht: montant_materiel_ht,
      montant_main_oeuvre_ht: parseFloat(newProjet.montant_main_oeuvre_ht) || 0,
      cout_achat_materiel: parseFloat(newProjet.cout_achat_materiel) || 0,
      cout_achat_pose: parseFloat(newProjet.cout_achat_pose) || 0,
      montant_total_ht: parseFloat(totaux.total_ht),
      montant_tva: parseFloat(totaux.total_tva),
      montant_ttc: parseFloat(totaux.total_ttc),
    };

    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/projets/${editingProjet.id}`, projetData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Projet mis à jour avec succès !");
      setShowEditDialog(false);
      setEditingProjet(null);
      fetchAll();
      resetForm();
    } catch (error) {
      console.error("Erreur mise à jour projet:", error);
      toast.error("Erreur lors de la mise à jour du projet");
    }
  };

  const resetForm = () => {
    setNewProjet({
      client_id: "",
      type_projet: "photovoltaique",
      titre: "",
      description: "",
      adresse_installation: "",
      code_postal: "",
      ville: "",
      puissance_kwc: 0,
      nombre_panneaux: 0,
      produits: [],
      commercial_id: user?.id || "",
      statut: "devis",
      montant_materiel_ht: 0,
      montant_main_oeuvre_ht: 0,
      cout_achat_materiel: 0,
      cout_achat_pose: 0,
    });
    setProduitsSelectionnes([]);
    setEditingProjet(null);
  };

  // Filtrer les projets
  const filteredProjets = projets.filter((projet) => {
    const matchSearch = 
      projet.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      projet.adresse_installation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      projet.ville?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchStatut = filterStatut === "all" || projet.statut === filterStatut;
    const matchType = filterType === "all" || projet.type_projet === filterType;
    
    return matchSearch && matchStatut && matchType;
  });

  // Statistiques
  const stats = {
    total: projets.length,
    devis: projets.filter(p => p.statut === "devis").length,
    en_cours: projets.filter(p => p.statut === "en_cours").length,
    termine: projets.filter(p => p.statut === "termine").length,
    ca_total: projets.reduce((sum, p) => sum + (p.montant_ttc || 0), 0),
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.prenom} ${client.nom}` : "Client inconnu";
  };

  const getCommercialName = (commercialId) => {
    const commercial = users.find(u => u.id === commercialId);
    return commercial ? `${commercial.first_name} ${commercial.last_name}` : "Non assigné";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const totaux = calculerTotaux();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Sun className="w-8 h-8 text-red-600" />
            Projets d'installation
          </h1>
          <p className="text-slate-600 mt-1">
            Gérez vos projets photovoltaïques, pompes à chaleur et rénovation énergétique
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-red-600 hover:bg-red-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau projet
        </Button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
            <p className="text-sm text-slate-600">Total projets</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">{stats.devis}</div>
            <p className="text-sm text-slate-600">Devis en attente</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-orange-600">{stats.en_cours}</div>
            <p className="text-sm text-slate-600">En cours</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{stats.termine}</div>
            <p className="text-sm text-slate-600">Terminés</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">
              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(stats.ca_total)}
            </div>
            <p className="text-sm text-slate-600">CA Total TTC</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterStatut}
              onChange={(e) => setFilterStatut(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-red-500"
            >
              <option value="all">Tous les statuts</option>
              {Object.entries(STATUTS).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-red-500"
            >
              <option value="all">Tous les types</option>
              {Object.entries(TYPES_PROJET).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Liste des projets */}
      <div className="grid grid-cols-1 gap-4">
        {filteredProjets.map((projet) => {
          const StatutConfig = STATUTS[projet.statut] || STATUTS.devis;
          const TypeConfig = TYPES_PROJET[projet.type_projet] || TYPES_PROJET.photovoltaique;
          
          return (
            <Card key={projet.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <TypeConfig.icon className={`w-5 h-5 ${TypeConfig.color}`} />
                      <h3 className="text-lg font-semibold text-slate-800">
                        {projet.titre || "Sans titre"}
                      </h3>
                      <Badge className={StatutConfig.color}>
                        {StatutConfig.label}
                      </Badge>
                      <Badge variant="outline">
                        {TypeConfig.label}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                      <div>
                        <p className="text-slate-500">Client</p>
                        <p className="font-medium text-slate-700">{getClientName(projet.client_id)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Adresse</p>
                        <p className="font-medium text-slate-700">
                          {projet.code_postal} {projet.ville}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Commercial</p>
                        <p className="font-medium text-slate-700">{getCommercialName(projet.commercial_id)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Montant TTC</p>
                        <p className="font-bold text-red-600">
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(projet.montant_ttc || 0)}
                        </p>
                      </div>
                    </div>

                    {projet.produits && projet.produits.length > 0 && (
                      <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm font-medium text-slate-700 mb-2">
                          Produits ({projet.produits.length}) :
                        </p>
                        <div className="space-y-1">
                          {projet.produits.slice(0, 3).map((prod, idx) => (
                            <p key={idx} className="text-xs text-slate-600">
                              • {prod.nom} (x{prod.quantite}) - {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(prod.prix_total_ht)} HT
                            </p>
                          ))}
                          {projet.produits.length > 3 && (
                            <p className="text-xs text-slate-500 italic">
                              ... et {projet.produits.length - 3} autre(s)
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 🆕 Bouton d'édition */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditProjet(projet)}
                    className="ml-4 text-blue-600 border-blue-600 hover:bg-blue-50"
                  >
                    Modifier
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {filteredProjets.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-center text-slate-500">
              <Sun className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun projet trouvé</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog Création Projet */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer un nouveau projet</DialogTitle>
            <DialogDescription>
              Remplissez les informations du projet et ajoutez les produits
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Type de projet *</Label>
                <select
                  value={newProjet.type_projet}
                  onChange={(e) => setNewProjet({...newProjet, type_projet: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                >
                  {Object.entries(TYPES_PROJET).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Statut du projet *</Label>
                <select
                  value={newProjet.statut}
                  onChange={(e) => setNewProjet({...newProjet, statut: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                >
                  {Object.entries(STATUTS).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Date d'installation</Label>
                <Input
                  type="date"
                  value={newProjet.date_installation}
                  onChange={(e) => setNewProjet({...newProjet, date_installation: e.target.value})}
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <Label>Client *</Label>
              <select
                value={newProjet.client_id}
                onChange={(e) => handleSelectClient(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                required
              >
                <option value="">Sélectionner un client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.prenom} {client.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Titre du projet *</Label>
              <Input
                value={newProjet.titre}
                onChange={(e) => setNewProjet({...newProjet, titre: e.target.value})}
                placeholder="Ex: Installation photovoltaïque 6kWc + VMC"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>Adresse installation *</Label>
                <Input
                  value={newProjet.adresse_installation}
                  onChange={(e) => setNewProjet({...newProjet, adresse_installation: e.target.value})}
                  placeholder="12 rue de la République"
                />
              </div>
              <div>
                <Label>Code postal *</Label>
                <Input
                  value={newProjet.code_postal}
                  onChange={(e) => setNewProjet({...newProjet, code_postal: e.target.value})}
                  placeholder="69000"
                />
              </div>
            </div>

            <div>
              <Label>Ville *</Label>
              <Input
                value={newProjet.ville}
                onChange={(e) => setNewProjet({...newProjet, ville: e.target.value})}
                placeholder="Lyon"
              />
            </div>

            {/* Section Produits */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-lg">Produits du projet *</Label>
                <Button
                  type="button"
                  onClick={() => setShowProduitsDialog(true)}
                  variant="outline"
                  className="text-red-600 border-red-600"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Ajouter des produits
                </Button>
              </div>

              {produitsSelectionnes.length === 0 ? (
                <div className="text-center p-6 bg-slate-50 rounded-lg border-2 border-dashed">
                  <ShoppingCart className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-600">Aucun produit ajouté</p>
                  <p className="text-sm text-slate-500">Cliquez sur "Ajouter des produits" pour commencer</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {produitsSelectionnes.map((prod) => (
                    <div key={prod.produit_id} className="p-3 bg-slate-50 rounded-lg border">
                      <div className="space-y-2">
                        {/* Ligne 1: Nom éditable */}
                        <div className="flex items-center gap-2">
                          <Input
                            value={prod.nom}
                            onChange={(e) => handleModifierNom(prod.produit_id, e.target.value)}
                            className="flex-1 font-medium text-sm"
                            placeholder="Nom du produit"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRetirerProduit(prod.produit_id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Ligne 2: Réf + Quantité + Prix unitaire + Total */}
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="text-xs text-slate-600">Réf: {prod.reference}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">Qté:</Label>
                            <Input
                              type="number"
                              min="1"
                              value={prod.quantite}
                              onChange={(e) => handleChangerQuantite(prod.produit_id, parseInt(e.target.value) || 0)}
                              className="w-16 h-8 text-center text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">Prix HT:</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={prod.prix_unitaire_ht}
                              onChange={(e) => handleModifierPrix(prod.produit_id, parseFloat(e.target.value) || 0)}
                              className="w-24 h-8 text-sm"
                            />
                            <span className="text-xs text-slate-500">€</span>
                          </div>
                          <div className="text-right min-w-[100px]">
                            <p className="font-semibold text-sm">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(prod.prix_total_ht)}</p>
                            <p className="text-xs text-slate-500">TVA {prod.taux_tva}%</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* 🆕 Section Coûts et Marges */}
                  <div className="p-4 bg-blue-50 rounded-lg space-y-3 mt-4 border-2 border-blue-200">
                    <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                      <Euro className="w-4 h-4" />
                      Coûts d'achat & Marges
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Coût achat matériel (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newProjet.cout_achat_materiel}
                          onChange={(e) => setNewProjet({...newProjet, cout_achat_materiel: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="mt-1"
                        />
                        <p className="text-xs text-blue-600 mt-1">
                          Marge: {((produitsSelectionnes.reduce((sum, p) => sum + p.prix_total_ht, 0) - (parseFloat(newProjet.cout_achat_materiel) || 0))).toFixed(2)} €
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm">Montant main d'œuvre HT (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newProjet.montant_main_oeuvre_ht}
                          onChange={(e) => setNewProjet({...newProjet, montant_main_oeuvre_ht: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Coût achat pose (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newProjet.cout_achat_pose}
                          onChange={(e) => setNewProjet({...newProjet, cout_achat_pose: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="mt-1"
                        />
                        <p className="text-xs text-blue-600 mt-1">
                          Marge pose: {((parseFloat(newProjet.montant_main_oeuvre_ht) || 0) - (parseFloat(newProjet.cout_achat_pose) || 0)).toFixed(2)} €
                        </p>
                      </div>
                      <div className="flex items-center justify-center">
                        <div className="text-center p-3 bg-white rounded border-2 border-blue-300 w-full">
                          <p className="text-xs text-blue-700 font-medium">Marge totale</p>
                          <p className="text-xl font-bold text-blue-900">
                            {((produitsSelectionnes.reduce((sum, p) => sum + p.prix_total_ht, 0) - (parseFloat(newProjet.cout_achat_materiel) || 0)) + ((parseFloat(newProjet.montant_main_oeuvre_ht) || 0) - (parseFloat(newProjet.cout_achat_pose) || 0))).toFixed(2)} €
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Totaux */}
                  <div className="p-4 bg-red-50 rounded-lg space-y-2 mt-4">
                    <div className="flex justify-between text-sm">
                      <span>Total HT:</span>
                      <span className="font-semibold">{totaux.total_ht} €</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total TVA:</span>
                      <span className="font-semibold">{totaux.total_tva} €</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total TTC:</span>
                      <span className="text-red-600">{totaux.total_ttc} €</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleCreateProjet}
                className="bg-red-600 hover:bg-red-700"
                disabled={!newProjet.client_id || !newProjet.titre || produitsSelectionnes.length === 0}
              >
                Créer le projet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Sélection Produits */}
      <Dialog open={showProduitsDialog} onOpenChange={setShowProduitsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sélectionner des produits</DialogTitle>
            <DialogDescription>
              Cliquez sur un produit pour l'ajouter au projet
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {produitsCatalogue.map((produit) => (
              <div
                key={produit.id}
                onClick={() => handleAjouterProduit(produit)}
                className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-800">{produit.nom}</h4>
                    <p className="text-sm text-slate-600 mt-1">Réf: {produit.reference}</p>
                    {produit.marque && (
                      <p className="text-xs text-slate-500 mt-1">Marque: {produit.marque}</p>
                    )}
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">TVA {produit.taux_tva}%</Badge>
                      {produit.eligible_cee && <Badge className="bg-blue-100 text-blue-800 text-xs">CEE</Badge>}
                      {produit.eligible_maprimerenov && <Badge className="bg-green-100 text-green-800 text-xs">MPR</Badge>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-red-600">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(produit.prix_vente_ht)}</p>
                    <p className="text-xs text-slate-500">HT / {produit.unite}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => setShowProduitsDialog(false)}>
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog Édition Projet (identique au dialog de création mais avec handleUpdateProjet) */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) {
          resetForm();
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le projet</DialogTitle>
            <DialogDescription>
              Modifiez les informations du projet et ses produits
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Type de projet *</Label>
                <select
                  value={newProjet.type_projet}
                  onChange={(e) => setNewProjet({...newProjet, type_projet: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                >
                  {Object.entries(TYPES_PROJET).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Statut du projet *</Label>
                <select
                  value={newProjet.statut}
                  onChange={(e) => setNewProjet({...newProjet, statut: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                >
                  {Object.entries(STATUTS).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Date d'installation</Label>
                <Input
                  type="date"
                  value={newProjet.date_installation}
                  onChange={(e) => setNewProjet({...newProjet, date_installation: e.target.value})}
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <Label>Client *</Label>
              <select
                value={newProjet.client_id}
                onChange={(e) => handleSelectClient(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                required
              >
                <option value="">Sélectionner un client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.prenom} {client.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Titre du projet *</Label>
              <Input
                value={newProjet.titre}
                onChange={(e) => setNewProjet({...newProjet, titre: e.target.value})}
                placeholder="Ex: Installation photovoltaïque 6kWc + VMC"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>Adresse installation *</Label>
                <Input
                  value={newProjet.adresse_installation}
                  onChange={(e) => setNewProjet({...newProjet, adresse_installation: e.target.value})}
                  placeholder="12 rue de la République"
                />
              </div>
              <div>
                <Label>Code postal *</Label>
                <Input
                  value={newProjet.code_postal}
                  onChange={(e) => setNewProjet({...newProjet, code_postal: e.target.value})}
                  placeholder="69000"
                />
              </div>
            </div>

            <div>
              <Label>Ville *</Label>
              <Input
                value={newProjet.ville}
                onChange={(e) => setNewProjet({...newProjet, ville: e.target.value})}
                placeholder="Lyon"
              />
            </div>

            {/* Section Produits - IDENTIQUE */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-lg">Produits du projet *</Label>
                <Button
                  type="button"
                  onClick={() => setShowProduitsDialog(true)}
                  variant="outline"
                  className="text-red-600 border-red-600"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Ajouter des produits
                </Button>
              </div>

              {produitsSelectionnes.length === 0 ? (
                <div className="text-center p-6 bg-slate-50 rounded-lg border-2 border-dashed">
                  <ShoppingCart className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-slate-600">Aucun produit ajouté</p>
                  <p className="text-sm text-slate-500">Cliquez sur "Ajouter des produits" pour commencer</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {produitsSelectionnes.map((prod) => (
                    <div key={prod.produit_id} className="p-3 bg-slate-50 rounded-lg border">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Input
                            value={prod.nom}
                            onChange={(e) => handleModifierNom(prod.produit_id, e.target.value)}
                            className="flex-1 font-medium text-sm"
                            placeholder="Nom du produit"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRetirerProduit(prod.produit_id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="text-xs text-slate-600">Réf: {prod.reference}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">Qté:</Label>
                            <Input
                              type="number"
                              min="1"
                              value={prod.quantite}
                              onChange={(e) => handleChangerQuantite(prod.produit_id, parseInt(e.target.value) || 0)}
                              className="w-16 h-8 text-center text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">Prix HT:</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={prod.prix_unitaire_ht}
                              onChange={(e) => handleModifierPrix(prod.produit_id, parseFloat(e.target.value) || 0)}
                              className="w-24 h-8 text-sm"
                            />
                            <span className="text-xs text-slate-500">€</span>
                          </div>
                          <div className="text-right min-w-[100px]">
                            <p className="font-semibold text-sm">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(prod.prix_total_ht)}</p>
                            <p className="text-xs text-slate-500">TVA {prod.taux_tva}%</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Section Coûts et Marges */}
                  <div className="p-4 bg-blue-50 rounded-lg space-y-3 mt-4 border-2 border-blue-200">
                    <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                      <Euro className="w-4 h-4" />
                      Coûts d'achat & Marges
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Coût achat matériel (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newProjet.cout_achat_materiel}
                          onChange={(e) => setNewProjet({...newProjet, cout_achat_materiel: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="mt-1"
                        />
                        <p className="text-xs text-blue-600 mt-1">
                          Marge: {((produitsSelectionnes.reduce((sum, p) => sum + p.prix_total_ht, 0) - (parseFloat(newProjet.cout_achat_materiel) || 0))).toFixed(2)} €
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm">Montant main d'œuvre HT (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newProjet.montant_main_oeuvre_ht}
                          onChange={(e) => setNewProjet({...newProjet, montant_main_oeuvre_ht: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Coût achat pose (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newProjet.cout_achat_pose}
                          onChange={(e) => setNewProjet({...newProjet, cout_achat_pose: parseFloat(e.target.value) || 0})}
                          placeholder="0.00"
                          className="mt-1"
                        />
                        <p className="text-xs text-blue-600 mt-1">
                          Marge pose: {((parseFloat(newProjet.montant_main_oeuvre_ht) || 0) - (parseFloat(newProjet.cout_achat_pose) || 0)).toFixed(2)} €
                        </p>
                      </div>
                      <div className="flex items-center justify-center">
                        <div className="text-center p-3 bg-white rounded border-2 border-blue-300 w-full">
                          <p className="text-xs text-blue-700 font-medium">Marge totale</p>
                          <p className="text-xl font-bold text-blue-900">
                            {((produitsSelectionnes.reduce((sum, p) => sum + p.prix_total_ht, 0) - (parseFloat(newProjet.cout_achat_materiel) || 0)) + ((parseFloat(newProjet.montant_main_oeuvre_ht) || 0) - (parseFloat(newProjet.cout_achat_pose) || 0))).toFixed(2)} €
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Totaux */}
                  <div className="p-4 bg-red-50 rounded-lg space-y-2 mt-4">
                    <div className="flex justify-between text-sm">
                      <span>Total HT:</span>
                      <span className="font-semibold">{totaux.total_ht} €</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total TVA:</span>
                      <span className="font-semibold">{totaux.total_tva} €</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total TTC:</span>
                      <span className="text-red-600">{totaux.total_ttc} €</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditDialog(false);
                  resetForm();
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleUpdateProjet}
                className="bg-red-600 hover:bg-red-700"
                disabled={!newProjet.client_id || !newProjet.titre || produitsSelectionnes.length === 0}
              >
                Enregistrer les modifications
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjetsPage;
